// src/pages/admin/AdminLayout.jsx
// git commit: "feat(AdminLayout): add Settings page to sidebar and mobile drawer"
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Zap,
  Wallet,
  History,
  Share2,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  ChevronRight,
  Settings,
  Gift,
  Mail,
       PlayCircle,
  Banknote,
     CreditCard,
} from "lucide-react";
import { useState, useEffect } from "react";

const NAV = [
  { to: "/admin/dashboard",    label: "Dashboard",    icon: LayoutDashboard },
  { to: "/admin/users",        label: "Users",        icon: Users },
  { to: "/admin/activations",  label: "Activations",  icon: Zap },
  { to: "/admin/wallets",      label: "Wallets",      icon: Wallet },
  { to: "/admin/transactions", label: "Transactions", icon: History },
  { to: "/admin/social-logs",  label: "Social Logs",  icon: Share2 },
  { to: "/admin/gifts",        label: "Gifts",        icon: Gift },
  { to: "/admin/broadcast",    label: "Broadcast",    icon: Mail },
  { to: "/admin/login-tutorials", label: "Login Tutorials",    icon: PlayCircle },
     { to: "/admin/gift-cards", label: "Gift Cards", icon: CreditCard },
     { to: "/admin/manual-funding", label: "Manual Funding", icon: Banknote },
  { to: "/admin/settings",     label: "Settings",     icon: Settings },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => {
    return localStorage.getItem("adminTheme") === "dark" ||
      window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("adminTheme", dark ? "dark" : "light");
  }, [dark]);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const logout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  // Active page label for mobile header
  const activeLabel = NAV.find((n) => location.pathname === n.to)?.label || "Admin";

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden">

      {/* ═══════════════════════════════════════════
          DESKTOP SIDEBAR
      ═══════════════════════════════════════════ */}
      <aside className="hidden md:flex flex-col w-60 bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 flex-shrink-0">

        {/* Logo */}
        <div className="px-5 pt-6 pb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
              L
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-none">
                LarryDigitals
              </p>
              <p className="text-xs text-gray-400 mt-0.5">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          <p className="px-3 pt-2 pb-1 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
            Menu
          </p>
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={17}
                    className={
                      isActive
                        ? "text-purple-600 dark:text-purple-400"
                        : "text-gray-400 dark:text-gray-500"
                    }
                  />
                  {label}
                  {isActive && (
                    <ChevronRight
                      size={14}
                      className="ml-auto text-purple-400 dark:text-purple-500"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Bottom actions */}
        <div className="px-3 pb-5 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-0.5">
          <button
            onClick={() => setDark(!dark)}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white transition"
          >
            {dark ? <Sun size={17} className="text-gray-400" /> : <Moon size={17} className="text-gray-400" />}
            {dark ? "Light mode" : "Dark mode"}
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
          >
            <LogOut size={17} />
            Logout
          </button>
        </div>
      </aside>

      {/* ═══════════════════════════════════════════
          MOBILE: TOP BAR + SLIDE DRAWER
      ═══════════════════════════════════════════ */}

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-30 h-14 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between px-4">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold text-xs">
            L
          </div>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {activeLabel}
          </span>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
        >
          <Menu size={20} className="text-gray-700 dark:text-gray-300" />
        </button>
      </div>

      {/* Mobile drawer overlay */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed top-0 right-0 bottom-0 w-72 z-50 overflow-y-auto overscroll-contain bg-white dark:bg-gray-900 shadow-2xl transform transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold text-sm">
              L
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-none">
                LarryDigitals
              </p>
              <p className="text-xs text-gray-400 mt-0.5">Admin Panel</p>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition"
          >
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Drawer nav */}
        <nav className="px-4 pt-4 space-y-0.5 flex-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    size={18}
                    className={
                      isActive ? "text-purple-600 dark:text-purple-400" : "text-gray-400"
                    }
                  />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Drawer footer */}
        <div className="mt-3 px-4 pb-28 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-0.5">
          <button
            onClick={() => setDark(!dark)}
            className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
          >
            {dark ? <Sun size={18} className="text-gray-400" /> : <Moon size={18} className="text-gray-400" />}
            {dark ? "Light mode" : "Dark mode"}
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════
          MAIN CONTENT
      ═══════════════════════════════════════════ */}
      <main className="flex-1 overflow-y-auto md:pt-0 pt-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}